import Box from "@mui/material/Box";
import InputAdornment from "@mui/material/InputAdornment";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import VisibilityIcon from "@mui/icons-material/VisibilityOutlined";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOffOutlined";
import EmailIcon from "@mui/icons-material/EmailOutlined";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import { useRef, useState } from "react";
import PersonIcon from "@mui/icons-material/PersonOutline";
import ReCAPTCHA from "react-google-recaptcha";
import { HttpStatusCode } from "axios";
import Alert from "@mui/material/Alert";
import PasswordStrengthBar from "react-password-strength-bar";
import { useTheme } from "@mui/material/styles";
import { useSignInSignUp } from "../../context/SignInSignUpProvider";
import { axiosHttp } from "../../AxiosWrapper";
import { AvatarUtil, isValidEmail } from "../../utils/CommonUtil";
import { useMediaQuery } from "@mui/material";

const SignUpView = () => {
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [inProgress, setInProgress] = useState(false);
    const [captchaError, showCaptchaError] = useState(false);
    const [formError, setFormError] = useState({});

    const { userData, setUserData } = useSignInSignUp();

    //Recaptcha
    const recaptchaRef = useRef(null);
    const recaptchaSiteKey = "6LeEWDosAAAAAEivd0AF00_hWTNMuNdHW3RkwiN_";

    const theme = useTheme();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
    const componentSize = isSmallScreen ? "small" :"medium";

    const onChangeInput = (e) => {
        const { name, value } = e.target;
        setUserData({ ...userData, [name]: value });
    }

    const validateSignUpData = () => {
        let errorMap = {};

        if (!userData.displayName || userData.displayName.trim() === "") {
            errorMap.displayName = "Please enter Display Name!";
        }
        let email = userData.email.trim();
        if (!userData.email || email === "") {
            errorMap.email = "Please enter Email!";
        } else if (!isValidEmail(email)) {
            errorMap.email = "Please enter a valid Email!";
        }


        if (!userData.password || userData.password.trim() === "") {
            errorMap.password = "Please enter Password!";
        }
        if (!userData.confirmPassword || userData.confirmPassword.trim() === "") {
            errorMap.confirmPassword = "Please enter Confirm Password!";
        }

        if ((!errorMap.password && !errorMap.confirmPassword) && userData.password != userData.confirmPassword) {
            errorMap.confirmPassword = "Password and Confirmation Password do not match!";
        }

        if (userData.passwordScore < 2) {
            errorMap.password = "Password is weak!"
        }

        setFormError(errorMap);
        return Object.keys(errorMap).length === 0;
    }

    const handleSignUp = async (event) => {
        event.preventDefault();
        const isValidData = validateSignUpData();
        if (recaptchaRef.current && isValidData) {
            setInProgress(true);
            try {
                const token = await recaptchaRef.current.executeAsync();
                const validateCaptchaResponse = await axiosHttp.post("/api/pub/dss/recaptcha/v1/validateCaptcha", { token: token, page: "signup" });
                if (validateCaptchaResponse.data.status === HttpStatusCode.Ok) {
                    userData.avatar = AvatarUtil.getAvatarColor();
                    axiosHttp.post("/api/pub/dss/v1/signUp", userData)
                        .then((signUpResponse) => {
                            if (signUpResponse.data.status === HttpStatusCode.Created) {
                                if (signUpResponse.data.content && signUpResponse.data.content.nextStep === "otp") {
                                    setUserData({ ...userData, state: "VERIFY_OTP", token: signUpResponse.data.content.token });
                                } else {
                                    setUserData({ ...userData, state: "CREATED" });
                                }
                            } else if (signUpResponse.data.status === HttpStatusCode.Conflict
                                || signUpResponse.data.status === HttpStatusCode.UnprocessableEntity) {
                                setFormError(signUpResponse.data.content);
                            }
                        })
                        .catch((error) => {
                            let a = error;
                        })
                        .then((res) => {
                            setInProgress(false);
                            return new Promise(function (resolve, reject) { resolve(); });
                        })

                } else {
                    showCaptchaError(true);
                    setInProgress(false);
                    return new Promise(function (resolve, reject) { resolve(); })
                }
            } catch (error) {
                showCaptchaError(true);
                setInProgress(false);
                return new Promise(function (resolve, reject) { resolve(); })
            } finally {
                if (recaptchaRef?.current && recaptchaRef.current.reset) {
                    recaptchaRef.current.reset();
                }
            }

        }
    }

    const reCaptchaOnChange = (value) => {
        recaptchaRef.current.reset();
    }

    const styles = {
        label: {
            fontWeight: 600,
            mb: .5
        },
        text: {
            '& .MuiOutlinedInput-root': {
                borderRadius: 3,
                '& fieldset': {
                    borderRadius: 3,
                }
            },
            '& input:-webkit-autofill': {
                '-webkit-box-shadow': '0 0 0 1000px transparent inset',
                '-webkit-text-fill-color': 'black',
                transition: 'background-color 5000s ease-in-out 0s',
            },
        },
        socialLoginbtn: {
            p: 2,
            borderRadius: 2
        }
    }
    return (
        <Box>

            <Stack spacing={2}>
                <Box>
                    <Typography sx={styles.label}>Display Name</Typography>
                    <TextField fullWidth sx={styles.text}
                        size={componentSize}
                        onChange={onChangeInput}
                        name="displayName"
                        value={userData.displayName}
                        type='text'
                        error={formError['displayName']}
                        helperText={formError['displayName']}
                        slotProps={{
                            input: {
                                endAdornment: (
                                    <InputAdornment position="end"><PersonIcon fontSize={componentSize} color="primary" /> </InputAdornment>
                                )
                            }
                        }}

                    />
                </Box>

                <Box>
                    <Typography sx={styles.label}>Email</Typography>
                    <TextField fullWidth sx={styles.text}
                        size={componentSize}
                        onChange={onChangeInput}
                        name="email"
                        type='text'
                        value={userData.email}
                        error={formError['email']}
                        helperText={formError['email']}
                        slotProps={{
                            input: {
                                endAdornment: (
                                    <InputAdornment position="end"><EmailIcon color="primary" fontSize={componentSize} /> </InputAdornment>
                                )
                            }
                        }}
                    />
                </Box>

                <Box>
                    <Typography sx={styles.label}>Password</Typography>
                    <TextField fullWidth sx={styles.text}
                        type={showPassword ? 'text' : 'password'}
                        size={componentSize}
                        onChange={onChangeInput}
                        name="password"
                        value={userData.password}
                        error={formError['password']}
                        helperText={formError['password']}
                        slotProps={{
                            input: {
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            onClick={() => setShowPassword(!showPassword)}
                                            onMouseDown={(e) => e.preventDefault()}
                                            onMouseUp={(e) => e.preventDefault()}
                                            edge="end"
                                            color="primary"
                                        >
                                            {showPassword ? <VisibilityIcon fontSize={componentSize} /> : <VisibilityOffIcon fontSize={componentSize} />}
                                        </IconButton>
                                    </InputAdornment>
                                )
                            }
                        }}
                    />
                    {userData.password &&
                        <Box sx={{ mt: 1.5, px: .5 }}>
                            <PasswordStrengthBar
                                password={userData.password}
                                shortScoreWord="Very short"
                                scoreWords={["very short", "weak", "medium", "strong", "very strong"]}
                                onChangeScore={(score) => setUserData({ ...userData, passwordScore: score })}
                            />
                        </Box>
                    }
                </Box>

                <Box>
                    <Typography sx={styles.label}>Confirm Password</Typography>
                    <TextField fullWidth sx={styles.text}
                        size={componentSize}
                        type={showConfirmPassword ? 'text' : 'password'}
                        onChange={onChangeInput}
                        name="confirmPassword"
                        value={userData.confirmPassword}
                        error={formError['confirmPassword']}
                        helperText={formError['confirmPassword']}
                        slotProps={{
                            input: {
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                            onMouseDown={(e) => e.preventDefault()}
                                            onMouseUp={(e) => e.preventDefault()}
                                            edge="end"
                                            color="primary"
                                        >
                                            {showConfirmPassword ? <VisibilityIcon fontSize={componentSize} /> : <VisibilityOffIcon fontSize={componentSize} />}
                                        </IconButton>
                                    </InputAdornment>
                                )
                            }
                        }}

                    />
                </Box>

                <ReCAPTCHA
                    size="invisible"
                    sitekey={recaptchaSiteKey}
                    ref={recaptchaRef}
                    onChange={reCaptchaOnChange}
                />
                {captchaError && <Alert severity="error" sx={{ borderRadius: 3 }}>Our system has detected automated activity and restricted access. If this is an error, please contact us.</Alert>}

                <Button loading={inProgress} onClick={handleSignUp} variant="contained" sx={{ py:{xs:.5, md:1.5}, borderRadius: 6 }}>
                    Sign Up
                </Button>
            </Stack>
        </Box>
    );
};

export default SignUpView;